import { useEffect, useMemo, useState } from 'react'
import cx from 'classnames'
import { LockableInput, Text } from 'components/atomic'
import { useUserId } from '@nhost/nextjs'
import { useUserMetadataQuery } from 'generated/graphql'
import { useGameAccountMutations, verifyGameAccount } from 'containers/onboarding/hooks'
import PlaystationIcon from '../../../../public/images/onboarding/game-playstation.svg'
import XboxIcon from '../../../../public/images/onboarding/game-xbox.svg'
import EpicGamesIcon from '../../../../public/images/onboarding/game-epic.svg'
import RiotIcon from '../../../../public/images/onboarding/game-riot.svg'
import BattleNetIcon from '../../../../public/images/onboarding/game-battle-net.svg'

const GameAccounts = {
  psn: { icon: PlaystationIcon, label: 'Playstation' },
  xbox: { icon: XboxIcon, label: 'Xbox' },
  epic: { icon: EpicGamesIcon, label: 'Epic Games' },
  riot: { icon: RiotIcon, label: 'Riot I.D.' },
  battle_net: { icon: BattleNetIcon, label: 'Battle.net' },
}

const GameAccountItem = ({ metadata, gameId, icon: SocialIcon, label, onChange, isSmallScreen }) => {
  const [locked, setLocked] = useState(false)
  const [value, setValue] = useState('')
  const [error, setError] = useState('')

  const [savedValue, isConnected] = useMemo(() => {
    const newValue = metadata[gameId] || ''
    setValue(newValue)
    setLocked(!!newValue)
    return [newValue, !!newValue]
  }, [metadata, gameId])

  const handleSubmit = async (newValue) => {
    // input not changed
    if (newValue === savedValue) {
      setLocked(!!newValue)
      return
    }

    setError('')
    setLocked(false)
    const updated = await onChange(gameId, newValue)
    if (!updated) {
      setError(
        'Username Not Valid' +
          (['psn'].includes(gameId) ? `. Ensure your ${gameId.toUpperCase()} account is not in private mode` : ''),
      )
    }
    setTimeout(() => {
      setLocked(!!newValue)
    })
  }

  const handleChange = (newValue) => {
    setError('')
    setValue(newValue)
  }

  const handleStartEdit = () => {
    setError('')
    setLocked(false)
  }

  return (
    <div className={`mb-10 ${isSmallScreen ? 'w-full 2lg:w-11/12' : 'w-full lg:w-[280px]'}`}>
      <div
        className={cx(
          !!error ? 'text-red-500' : isConnected ? 'text-secondary' : 'text-white',
          'mb-1.5 mr-2 flex h-4 items-end',
        )}
      >
        <SocialIcon className="mr-1 h-4 w-4" />
        <Text tag="span" variant="body2" className="mr-auto uppercase">
          {isConnected ? `${label} linked` : `connect ${label}`}
        </Text>
      </div>

      <LockableInput
        type="text"
        label=""
        placeholder="Type Username"
        locked={locked}
        value={value}
        error={error}
        onChange={handleChange}
        onSubmit={handleSubmit}
        onStartEdit={handleStartEdit}
      />
    </div>
  )
}
interface GameConnectionProps {
  isSmallScreen?: boolean
}
export default function GameConnection({ isSmallScreen }: GameConnectionProps) {
  const userId = useUserId()
  const { loading, data, refetch } = useUserMetadataQuery()
  const [updaters, loadings] = useGameAccountMutations()
  const [metadata, setMetadata] = useState({})

  useEffect(() => {
    const response = data?.users_metadata[0]
    if (response) {
      setMetadata(response)
    }
  }, [data])

  const handleChange = async (gameId, newValue) => {
    const isValid = await verifyGameAccount(gameId, newValue)

    if (!isValid) {
      refetch()
      return false
    }

    if (updaters && typeof updaters[gameId] === 'function') {
      try {
        await updaters[gameId]({
          variables: {
            userId,
            username: newValue ? newValue : null,
          },
        })
        return true
      } catch (err) {
        return false
      } finally {
        refetch()
      }
    }
  }

  return (
    <>
      {Object.entries(GameAccounts).map(([key, attrs]) => (
        <GameAccountItem
          key={key}
          gameId={key}
          {...attrs}
          metadata={metadata}
          onChange={handleChange}
          isSmallScreen={isSmallScreen}
        />
      ))}
    </>
  )
}
